import { LitElement, css, html } from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'

class NovaQuantityInput extends LitElement {
  static properties = {
    key: {},
    label: {},
    initial: {
      type: Number,
    },
    value: {
      type: Number,
    },
    min: {
      type: Number,
    },
    max: {
      type: String,
      attribute: 'max',
    },
  }

  constructor() {
    super()

    this.broadcast = this.broadcast.bind(this)
    this.id = Math.random() * 100000
    this.max = ''
    this.value = this.initial
  }

  connectedCallback() {
    super.connectedCallback()
    this.value = this.initial
    this.updateContent = this.updateContent.bind(this)
    document.addEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  updateContent(event) {
    const newMax = event.detail.html?.getAttribute('max')
    if (!newMax) return
    
    this.setAttribute('max', newMax)
    this.max = newMax

    const maxNumber = Number(newMax)

    if (this.value > maxNumber) {
      this.value = maxNumber
      this.broadcast()
    } else {
      this.value = 1
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initial')) {
      this.value = this.initial
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('key')) {
      this.value = this.initial;
    }
  }

  broadcast() {
    const $input = this.renderRoot.querySelector('slot[name="input"]')

    if ($input) {
      $input.value = this.value
    }

    this.dispatchEvent(
      new CustomEvent('changeQuantityStarted', {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          value: this.value,
        },
      }),
    )
  }

  increment() {
    if (this.max !== '' && this.value >= Number(this.max)) {
      return
    }
    this.value += 1
    this.broadcast()
  }

  decrement() {
    if (this.value < this.min) {
      return
    }

    this.value -= 1
    this.broadcast()
  }

  override(e) {
    let value = Math.max(0, e.target.value)
    if (this.max !== '' && value > Number(this.max)) {
      value = this.max
    }
    this.value = value
    this.broadcast()
  }

  render() {
    return html`
      <span>
        <div class="wrapper" part="qty-wrapper">
          <button
            @click=${this.decrement}
            name="decrease"
            ?disabled=${this.value <= this.min}
            class="decrease"
            part="qty-decrease-btn"
            aria-label="${window.cartStrings.decreaseQuantity}"
          >
            ${paths.minus}
          </button>
          <input
            id="Quantity-${this.id}"
            class="input"
            type="number"
            name="updates[]"
            .value=${this.value}
            @change=${this.override}
            min=${this.min}
            max=${this.max}
            aria-label=${this.value > 1 ? `${window.cartStrings.items}` : `${window.cartStrings.item}`}
          />
          <button
            @click=${this.increment} 
            name="increase" 
            class="increase" 
            part="qty-increase-btn"
            aria-label="${window.cartStrings?.increaseQuantity }"
            >
            ${paths.plus}
          </button>
        </div>
      </span>
    `
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      .wrapper {
        border: 1px solid var(--nova-brand-neutral);
        border-radius: var(--nova-border-radius-s);
        box-sizing: border-box;
        color: #000;
        position: relative;
        display: flex;
        height: var(--nova-forms-input-height-lg);
      }
      .input {
        color: currentColor;
        font: var(--nova-body-font);
        text-align: center;
        border: 0;
        width: 100%;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;
      }
      button {
        height: var(--nova-forms-input-height-lg);
        width: calc(var(--nova-space-sm) + var(--nova-space-xs));
        flex-shrink: 0;
        border: 0;
        background-color: var(--nova-color-brand-reverse);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000;
      }
      .decrease {
        padding: 0 0 0 var(--nova-space-sm);
      }
      .decrease[disabled] {
        color: var(--nova-color-brand-taupe);
        cursor: not-allowed;
      }
      .increase {
        padding: 0 var(--nova-space-sm) 0 0;
      }
      button:hover {
        color: #000;
      }
      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--color-background) inset !important;
      }
      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      :host(.cart-item__quantity-input) .input:focus,
      :host(.cart-item__quantity-input) .input:focus-visible {
        outline: 0;
      }
    `,
  ]
}

customElements.define('nova-quantity-input', NovaQuantityInput)
